import React, { useMemo, useState } from "react";
import { useI18n } from "../i18n";

export interface PaginatedListProps<T> {
  items: T[];
  searchKey?: (item: T) => string;
  renderItem: (item: T, index: number) => React.ReactNode;
  pageSize?: number;
  emptyLabel?: string;
  searchPlaceholder?: string;
  showSearch?: boolean;
  overflow?: boolean;
  overflowNotice?: React.ReactNode;
  headerRight?: React.ReactNode;
}

export function PaginatedList<T>({
  items,
  searchKey,
  renderItem,
  pageSize = 10,
  emptyLabel,
  searchPlaceholder,
  showSearch = true,
  overflow = false,
  overflowNotice,
  headerRight,
}: PaginatedListProps<T>) {
  const { t } = useI18n();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const [mode, setMode] = useState<"loadMore" | "paged">("loadMore");

  const filteredItems = useMemo(() => {
    if (!search.trim() || !searchKey) return items;
    const q = search.trim().toLowerCase();
    return items.filter((item) => searchKey(item).toLowerCase().includes(q));
  }, [items, search, searchKey]);

  if (!items || items.length === 0) {
    return <div className="empty-chart">{emptyLabel || t.common.noDataFound}</div>;
  }

  const total = filteredItems.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);

  const displayedItems = mode === "paged"
    ? filteredItems.slice((currentPage - 1) * pageSize, currentPage * pageSize)
    : filteredItems.slice(0, visibleCount);

  return (
    <div className="paginated-container">
      {overflow && (
        <div className="badge warn" style={{ marginBottom: 8 }}>
          {overflowNotice || "存在 __other_overflow__ 降级分组: 榜单非全量精确 Top K"}
        </div>
      )}

      {(showSearch && items.length > 5 || headerRight) && (
        <div className="paginated-toolbar">
          {showSearch && searchKey ? (
            <div className="search-input-wrapper">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                className="search-input"
                value={search}
                placeholder={searchPlaceholder || t.common.searchPlaceholder}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                  setVisibleCount(pageSize);
                }}
              />
              {search && (
                <button
                  type="button"
                  className="search-clear-btn"
                  onClick={() => setSearch("")}
                >
                  ✕
                </button>
              )}
            </div>
          ) : <div />}

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {headerRight}
            {total > pageSize && (
              <button
                type="button"
                className="mode-toggle-btn"
                title={mode === "loadMore" ? "切换至数字分页" : "切换至连续加载"}
                onClick={() => setMode((m) => (m === "loadMore" ? "paged" : "loadMore"))}
              >
                {mode === "loadMore" ? "📄 分页" : "📜 流式"}
              </button>
            )}
          </div>
        </div>
      )}

      {displayedItems.length === 0 ? (
        <div className="empty-chart" style={{ padding: "16px 0" }}>
          {t.common.noDataFound}
        </div>
      ) : (
        <div className="barlist">
          {displayedItems.map((item, idx) => renderItem(item, idx))}
        </div>
      )}

      {/* Footer controls: Load more vs Pagination */}
      {total > pageSize && (
        <div className="paginated-footer">
          <span className="muted" style={{ fontSize: 12 }}>
            {t.common.showingTotal} {total}
          </span>

          {mode === "loadMore" ? (
            <div style={{ display: "flex", gap: 8, width: "100%", marginTop: 8 }}>
              {visibleCount < total ? (
                <button
                  type="button"
                  className="load-more-btn"
                  onClick={() => setVisibleCount((prev) => Math.min(total, prev + pageSize))}
                >
                  {t.common.loadMore} (+{Math.min(pageSize, total - visibleCount)})
                </button>
              ) : (
                <button
                  type="button"
                  className="load-more-btn"
                  style={{ background: "#f1f5f9", color: "#64748b", borderColor: "#e2e8f0" }}
                  onClick={() => setVisibleCount(pageSize)}
                >
                  {t.common.collapse}
                </button>
              )}
            </div>
          ) : (
            <div className="pagination-controls">
              <button
                type="button"
                className="pagination-btn"
                disabled={currentPage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                {t.common.prevPage}
              </button>
              <span style={{ fontSize: 12, padding: "0 6px" }}>
                {t.common.page}{currentPage}{t.common.ofPage}{totalPages}
              </span>
              <button
                type="button"
                className="pagination-btn"
                disabled={currentPage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                {t.common.nextPage}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
